import { useState, useRef, useEffect, useMemo } from 'react';
import { seriesEndMs } from '@fias/calendar-recurrence';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import {
  Upload,
  FileText,
  Check,
  AlertCircle,
  Loader2,
  Repeat,
  ChevronRight,
  ArrowDownWideNarrow,
  CalendarClock,
  Plus,
} from 'lucide-react';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '@/components/ui/collapsible';
import { toast } from '@/components/ui/use-toast';
import {
  api,
  CalendarParsedImportResult,
  CalendarImportedEvent,
  CalendarCollectionDetail,
} from '@/lib/api';
import { ColorPicker } from './ColorPicker';

interface EventGroup {
  title: string;
  count: number;
  isRecurring: boolean;
  earliestStart: string;
}

type SortMode = 'frequency' | 'upcoming';

function groupEventsByTitle(events: CalendarImportedEvent[]): EventGroup[] {
  const map = new Map<string, { count: number; isRecurring: boolean; earliestStart: string }>();
  for (const e of events) {
    const existing = map.get(e.title);
    if (existing) {
      existing.count++;
      if (e.isRecurring) existing.isRecurring = true;
      if (e.startAt < existing.earliestStart) existing.earliestStart = e.startAt;
    } else {
      map.set(e.title, { count: 1, isRecurring: !!e.isRecurring, earliestStart: e.startAt });
    }
  }
  return Array.from(map.entries()).map(([title, { count, isRecurring, earliestStart }]) => ({
    title,
    count,
    isRecurring,
    earliestStart,
  }));
}

function startOfToday(): string {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

/**
 * Check whether a recurring event's rrule extends into the future — via the
 * shared, library-backed `seriesEndMs` (this used to be the arche's FOURTH
 * hand-rolled RRULE reader, estimating a COUNT end with frequency
 * arithmetic; the shared evaluator expands the actual rule). `null` means
 * "never ends" or "could not be safely evaluated" — both include-to-be-safe.
 */
function recurringExtendsToFuture(event: CalendarImportedEvent, todayIso: string): boolean {
  if (!event.isRecurring || !event.rrule) return false;
  const endMs = seriesEndMs(event.rrule, { startIso: event.startAt });
  if (endMs === null) return true;
  return endMs >= Date.parse(todayIso);
}

interface ImportDialogProps {
  open: boolean;
  onClose: () => void;
  calendars: CalendarCollectionDetail[];
  onImportComplete: () => void;
  onCreateCalendar?: () => void;
}

type ImportStep = 'upload' | 'preview' | 'importing' | 'done';

export function ImportDialog({
  open,
  onClose,
  calendars,
  onImportComplete,
  onCreateCalendar,
}: ImportDialogProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [step, setStep] = useState<ImportStep>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [parseResult, setParseResult] = useState<CalendarParsedImportResult | null>(null);
  const [calendarId, setCalendarId] = useState('');
  const [duplicateStrategy, setDuplicateStrategy] = useState<'skip' | 'overwrite' | 'create_new'>(
    'skip',
  );
  const [defaultColor, setDefaultColor] = useState<string | null>(null);
  const [groupColors, setGroupColors] = useState<Map<string, string>>(new Map());
  const [expandedGroupTitle, setExpandedGroupTitle] = useState<string | null>(null);
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [sortMode, setSortMode] = useState<SortMode>('frequency');

  // Set calendarId when dialog opens with available calendars
  useEffect(() => {
    if (open && calendars.length > 0 && !calendarId) {
      setCalendarId(calendars[0].id);
    }
  }, [open, calendars, calendarId]);

  // Auto-select newly created calendar
  const prevCalendarCount = useRef(calendars.length);
  useEffect(() => {
    if (open && calendars.length > prevCalendarCount.current && calendars.length > 0) {
      setCalendarId(calendars[calendars.length - 1].id);
    }
    prevCalendarCount.current = calendars.length;
  }, [open, calendars]);

  const allEventGroups = useMemo(
    () => (parseResult ? groupEventsByTitle(parseResult.events) : []),
    [parseResult],
  );

  const todayIso = useMemo(() => startOfToday(), []);

  const eventGroups = useMemo(() => {
    if (sortMode === 'upcoming') {
      if (!parseResult) return [];
      // Include events starting today or later, plus recurring events whose rrule extends into the future
      const upcomingEvents = parseResult.events.filter(
        (e) => e.startAt >= todayIso || recurringExtendsToFuture(e, todayIso),
      );
      const groups = groupEventsByTitle(upcomingEvents);
      return groups.sort((a, b) => b.count - a.count);
    }
    // Default: sort by frequency descending
    return [...allEventGroups].sort((a, b) => b.count - a.count);
  }, [sortMode, allEventGroups, parseResult, todayIso]);

  const [parsing, setParsing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<{
    imported: number;
    skipped: number;
    updated: number;
    errors: string[];
  } | null>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;

    setFile(f);
    setParsing(true);

    try {
      const result = await api.parseCalendarImport(f);
      setParseResult(result);
      setStep('preview');
    } catch (err) {
      toast({
        title: 'Parse failed',
        description: err instanceof Error ? err.message : 'Failed to parse file',
        variant: 'destructive',
      });
    } finally {
      setParsing(false);
    }
  };

  const handleImport = async () => {
    if (!parseResult || !calendarId) return;

    setImporting(true);
    setStep('importing');

    try {
      // Apply color priority: group-specific > default > original from ICS
      const events = parseResult.events.map((e) => {
        const color = groupColors.get(e.title) ?? defaultColor ?? e.color ?? null;
        return { ...e, color };
      });

      const result = await api.confirmCalendarImport({
        calendarId,
        events,
        duplicateStrategy,
      });
      setImportResult(result);
      setStep('done');
      onImportComplete();
    } catch (err) {
      toast({
        title: 'Import failed',
        description: err instanceof Error ? err.message : 'Failed to import events',
        variant: 'destructive',
      });
      setStep('preview');
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    setStep('upload');
    setFile(null);
    setParseResult(null);
    setImportResult(null);
    setCalendarId('');
    setDefaultColor(null);
    setGroupColors(new Map());
    setExpandedGroupTitle(null);
    setCustomizeOpen(false);
    setSortMode('frequency');
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="max-w-md max-h-[85vh] flex flex-col gap-4">
        <DialogHeader className="shrink-0">
          <DialogTitle>Import Calendar</DialogTitle>
          <DialogDescription>Import events from an .ics (iCalendar) file.</DialogDescription>
        </DialogHeader>

        {/* Step 1: Upload */}
        {step === 'upload' && (
          <div className="py-4">
            <input
              ref={fileInputRef}
              type="file"
              accept=".ics,.ical"
              className="hidden"
              onChange={handleFileChange}
            />

            <div
              className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-primary/50 transition-colors"
              onClick={() => fileInputRef.current?.click()}
            >
              {parsing ? (
                <div className="flex flex-col items-center gap-2">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Parsing file...</span>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <Upload className="h-8 w-8 text-muted-foreground" />
                  <span className="text-sm font-medium">Click to select .ics file</span>
                  <span className="text-xs text-muted-foreground">
                    Supports Google Calendar, Apple Calendar, Outlook exports
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Step 2: Preview */}
        {step === 'preview' && parseResult && (
          <div className="space-y-4 py-2 overflow-y-auto min-h-0">
            {/* File info */}
            <div className="flex items-center gap-2 bg-muted/50 rounded px-3 py-2">
              <FileText className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">{file?.name}</span>
              <span className="ml-auto text-xs text-muted-foreground">
                {parseResult.totalParsed} events
              </span>
            </div>

            {/* Calendar name from file */}
            {parseResult.calendarName && (
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                Source calendar:
                {parseResult.calendarColor && (
                  <span
                    className="w-2.5 h-2.5 rounded-full inline-block"
                    style={{ backgroundColor: parseResult.calendarColor }}
                  />
                )}
                {parseResult.calendarName}
              </div>
            )}

            {/* Warnings */}
            {parseResult.warnings.length > 0 && (
              <div className="bg-yellow-50 dark:bg-yellow-950/20 rounded px-3 py-2">
                <div className="text-xs font-medium text-yellow-800 dark:text-yellow-200 mb-1">
                  Warnings
                </div>
                {parseResult.warnings.slice(0, 5).map((w, i) => (
                  <div key={i} className="text-xs text-yellow-700 dark:text-yellow-300">
                    {w}
                  </div>
                ))}
                {parseResult.warnings.length > 5 && (
                  <div className="text-xs text-yellow-600 dark:text-yellow-400 mt-1">
                    ...and {parseResult.warnings.length - 5} more
                  </div>
                )}
              </div>
            )}

            {/* Target calendar */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-xs">Import into calendar</Label>
                {onCreateCalendar && (
                  <button
                    type="button"
                    className="flex items-center gap-0.5 text-xs text-muted-foreground hover:text-foreground"
                    onClick={onCreateCalendar}
                  >
                    <Plus className="h-3 w-3" />
                    Add
                  </button>
                )}
              </div>
              <Select value={calendarId} onValueChange={setCalendarId}>
                <SelectTrigger className="text-sm">
                  <SelectValue placeholder="Select calendar" />
                </SelectTrigger>
                <SelectContent>
                  {calendars.map((cal) => (
                    <SelectItem key={cal.id} value={cal.id}>
                      <span className="flex items-center gap-2">
                        <span
                          className="w-2.5 h-2.5 rounded-full"
                          style={{ backgroundColor: cal.color }}
                        />
                        {cal.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Event colors */}
            <div className="border rounded-lg p-3 space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-medium">Event colors</Label>
                {(defaultColor || groupColors.size > 0) && (
                  <button
                    type="button"
                    className="text-xs text-muted-foreground hover:text-foreground"
                    onClick={() => {
                      setDefaultColor(null);
                      setGroupColors(new Map());
                      setExpandedGroupTitle(null);
                    }}
                  >
                    Clear all
                  </button>
                )}
              </div>

              {/* Default color row */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs text-muted-foreground">Default color</span>
                  {defaultColor && (
                    <button
                      type="button"
                      className="text-xs text-muted-foreground hover:text-foreground"
                      onClick={() => setDefaultColor(null)}
                    >
                      None
                    </button>
                  )}
                </div>
                {!defaultColor ? (
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full text-xs text-muted-foreground"
                    onClick={() => setDefaultColor('#3b82f6')}
                  >
                    Set a default color for all events
                  </Button>
                ) : (
                  <ColorPicker value={defaultColor} onChange={setDefaultColor} />
                )}
                <p className="text-[11px] text-muted-foreground mt-1">
                  Applied to events without a specific color
                </p>
              </div>

              {/* Customize by event group */}
              {allEventGroups.length > 0 && (
                <Collapsible open={customizeOpen} onOpenChange={setCustomizeOpen}>
                  <CollapsibleTrigger className="flex items-center gap-1.5 text-xs font-medium hover:text-foreground text-muted-foreground w-full">
                    <ChevronRight
                      className={`h-3.5 w-3.5 transition-transform ${customizeOpen ? 'rotate-90' : ''}`}
                    />
                    Customize by event ({eventGroups.length} unique)
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    {/* Sort toggle */}
                    <div className="mt-2 flex items-center gap-1">
                      <button
                        type="button"
                        className={`flex items-center gap-1 rounded px-2 py-1 text-[11px] transition-colors ${
                          sortMode === 'frequency'
                            ? 'bg-muted text-foreground font-medium'
                            : 'text-muted-foreground hover:text-foreground'
                        }`}
                        onClick={() => setSortMode('frequency')}
                      >
                        <ArrowDownWideNarrow className="h-3 w-3" />
                        Most frequent
                      </button>
                      <button
                        type="button"
                        className={`flex items-center gap-1 rounded px-2 py-1 text-[11px] transition-colors ${
                          sortMode === 'upcoming'
                            ? 'bg-muted text-foreground font-medium'
                            : 'text-muted-foreground hover:text-foreground'
                        }`}
                        onClick={() => setSortMode('upcoming')}
                      >
                        <CalendarClock className="h-3 w-3" />
                        Upcoming
                      </button>
                    </div>

                    {eventGroups.length === 0 ? (
                      <div className="mt-2 border rounded px-3 py-4 text-center text-xs text-muted-foreground">
                        No upcoming events found
                      </div>
                    ) : (
                      <div className="mt-2 max-h-[200px] overflow-y-auto space-y-0.5 border rounded">
                        {eventGroups.map((group) => {
                          const groupColor = groupColors.get(group.title);
                          const isExpanded = expandedGroupTitle === group.title;

                          return (
                            <div key={group.title} className="px-2 py-1.5">
                              <div className="flex items-center gap-2">
                                {/* Color swatch button */}
                                <button
                                  type="button"
                                  className={`w-5 h-5 rounded-full shrink-0 transition-all ${
                                    groupColor
                                      ? 'border-2 border-foreground/30'
                                      : 'border-2 border-dashed border-muted-foreground/40'
                                  }`}
                                  style={groupColor ? { backgroundColor: groupColor } : undefined}
                                  onClick={() =>
                                    setExpandedGroupTitle(isExpanded ? null : group.title)
                                  }
                                  aria-label={`Set color for ${group.title}`}
                                />
                                {/* Title */}
                                <span className="text-xs truncate flex-1" title={group.title}>
                                  {group.title}
                                </span>
                                {/* Recurring icon + count */}
                                <span className="flex items-center gap-1 text-[11px] text-muted-foreground shrink-0">
                                  {group.isRecurring && <Repeat className="h-3 w-3" />}
                                  <span>&times;{group.count}</span>
                                </span>
                              </div>
                              {/* Inline color picker */}
                              {isExpanded && (
                                <div className="mt-1.5 pl-7">
                                  <div className="flex items-center gap-2">
                                    <ColorPicker
                                      value={groupColor || '#3b82f6'}
                                      onChange={(color) => {
                                        setGroupColors((prev) => {
                                          const next = new Map(prev);
                                          next.set(group.title, color);
                                          return next;
                                        });
                                        setExpandedGroupTitle(null);
                                      }}
                                    />
                                  </div>
                                  {groupColor && (
                                    <button
                                      type="button"
                                      className="text-[11px] text-muted-foreground hover:text-foreground mt-1"
                                      onClick={() => {
                                        setGroupColors((prev) => {
                                          const next = new Map(prev);
                                          next.delete(group.title);
                                          return next;
                                        });
                                        setExpandedGroupTitle(null);
                                      }}
                                    >
                                      Remove color
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CollapsibleContent>
                </Collapsible>
              )}
            </div>

            {/* Duplicate strategy */}
            <div>
              <Label className="text-xs">If duplicates found</Label>
              <Select
                value={duplicateStrategy}
                onValueChange={(v) => setDuplicateStrategy(v as typeof duplicateStrategy)}
              >
                <SelectTrigger className="text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="skip">Skip duplicates</SelectItem>
                  <SelectItem value="overwrite">Overwrite existing</SelectItem>
                  <SelectItem value="create_new">Create new copies</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {/* Step 3: Importing */}
        {step === 'importing' && (
          <div className="py-8 flex flex-col items-center gap-3">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <span className="text-sm text-muted-foreground">Importing events...</span>
          </div>
        )}

        {/* Step 4: Done */}
        {step === 'done' && importResult && (
          <div className="py-4 space-y-3">
            <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
              <Check className="h-5 w-5" />
              <span className="font-medium">Import complete</span>
            </div>

            <div className="text-sm space-y-1">
              <div>Imported: {importResult.imported}</div>
              {importResult.skipped > 0 && <div>Skipped (duplicates): {importResult.skipped}</div>}
              {importResult.updated > 0 && <div>Updated: {importResult.updated}</div>}
            </div>

            {importResult.errors.length > 0 && (
              <div className="bg-destructive/10 rounded px-3 py-2">
                <div className="flex items-center gap-1 text-xs font-medium text-destructive mb-1">
                  <AlertCircle className="h-3 w-3" />
                  {importResult.errors.length} errors
                </div>
                {importResult.errors.slice(0, 3).map((e, i) => (
                  <div key={i} className="text-xs text-destructive/80">
                    {e}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <DialogFooter className="shrink-0">
          {step === 'preview' && (
            <>
              <Button variant="outline" onClick={() => setStep('upload')}>
                Back
              </Button>
              <Button onClick={handleImport} disabled={!calendarId || importing}>
                Import {parseResult?.totalParsed} events
              </Button>
            </>
          )}
          {step === 'done' && <Button onClick={handleClose}>Done</Button>}
          {step === 'upload' && (
            <Button variant="outline" onClick={handleClose}>
              Cancel
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
